import type React from 'react';
import { Badge, Group, Title, ActionIcon, Menu } from '@mantine/core';
import {
  IconArchive,
  IconArchiveOff,
  IconCopy,
  IconDots,
  IconEdit,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import type { DealExtended } from '../DealViewController';

interface DealHeaderProps {
  deal: DealExtended;
  canEdit?: boolean;
  canDelete?: boolean;
  permissionsLoading?: boolean;
  onArchive?: () => void;
  onUnarchive?: () => void;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onCancel?: () => void;
  onDelete?: () => void;
  /** Дополнительные действия от клиентских модулей (слот расширения) */
  extraActions?: React.ReactNode;
}

export function DealHeader({
  deal,
  canEdit = false,
  canDelete = false,
  permissionsLoading = false,
  onArchive,
  onUnarchive,
  onEdit,
  onDuplicate,
  onCancel,
  onDelete,
  extraActions,
}: DealHeaderProps) {
  const status = deal.deal_status;
  const rowsTotal = deal.rows_total != null ? Number(deal.rows_total) : null;
  const dealAmount = deal.amount != null ? Number(deal.amount) : null;
  const effectiveAmount = rowsTotal ?? dealAmount;
  const hasAmount = effectiveAmount != null && effectiveAmount > 0;
  const partnerLabel = deal.partner?.name ?? deal.partner?.full_name ?? 'Партнер';
  const amountLabel = hasAmount
    ? new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(effectiveAmount)
    : null;

  const archivedPrefix = deal.archived ? '[Архивная] ' : '';
  const dealNumber = deal.number || `#${deal.id}`;
  // Название сделки обрезается многоточием при нехватке места,
  // сумма — отдельный блок без сжатия, поэтому всегда видна целиком.
  const dealTitle = `${archivedPrefix}${dealNumber} — Сделка ${partnerLabel}`;
  const amountSuffix = amountLabel ? `на ${amountLabel} ₽` : null;

  return (
    <div style={{ minWidth: 0, overflow: 'hidden' }}>
      <Group gap='sm' align='center' wrap='nowrap' justify='space-between'>
        <Group gap={6} align='center' wrap='nowrap' style={{ minWidth: 0, flex: 1 }}>
          <Title
            order={3}
            style={{
              fontSize: 18,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            {dealTitle}
          </Title>
          {amountSuffix && (
            <Title order={3} style={{ fontSize: 18, whiteSpace: 'nowrap', flexShrink: 0 }}>
              {amountSuffix}
            </Title>
          )}
        </Group>
        <Group gap='sm' align='center' style={{ flexShrink: 0 }}>
          {status && (
            <Badge
              color={status.color || 'gray'}
              variant='light'
              size='sm'
              style={{
                textTransform: 'none',
                fontWeight: 400,
                backgroundColor: status.color ? `${status.color}20` : undefined,
                color: status.color || undefined,
              }}
            >
              {status.name}
            </Badge>
          )}
          {extraActions}
          {(canEdit || canDelete || onUnarchive || onDuplicate) && (
            <Menu withArrow position='bottom-end' withinPortal>
              <Menu.Target>
                <ActionIcon
                  variant='light'
                  size='md'
                  disabled={permissionsLoading}
                  aria-label='Действия'
                >
                  <IconDots size={18} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {onDuplicate && (
                  <Menu.Item leftSection={<IconCopy size={14} />} onClick={onDuplicate}>
                    Создать копию
                  </Menu.Item>
                )}
                {canEdit && onArchive && (
                  <Menu.Item leftSection={<IconArchive size={14} />} onClick={onArchive}>
                    Архивировать
                  </Menu.Item>
                )}
                {onUnarchive && (
                  <Menu.Item leftSection={<IconArchiveOff size={14} />} onClick={onUnarchive}>
                    Убрать из архива
                  </Menu.Item>
                )}
                {canEdit && onEdit && (
                  <Menu.Item leftSection={<IconEdit size={14} />} onClick={onEdit}>
                    Редактировать
                  </Menu.Item>
                )}
                {canEdit && onCancel && (
                  <Menu.Item leftSection={<IconX size={14} />} color='orange' onClick={onCancel}>
                    Отменить
                  </Menu.Item>
                )}
                {canDelete && onDelete && (
                  <Menu.Item leftSection={<IconTrash size={14} />} color='red' onClick={onDelete}>
                    Удалить
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      </Group>
    </div>
  );
}
